import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { VALID_ORDER_STATUSES } from '@wooconnect/shared';
import { useAnalyticsSettings } from './useAnalytics';

export interface OrderStatusChoice {
  value: string;
  label: string;
}

/**
 * The order statuses that can actually be set on the active store.
 *
 * Stores may register custom statuses (Olympus GR has «ΣΕ ΑΝΑΜΟΝΗ +»,
 * «ΣΕ ΕΚΚΡΕΜΟΤΗΤΑ +», «ΑΚΥΡΩΜΕΝΗ +»), so the list must come from the store
 * rather than from a hardcoded constant. It is already synced by the bridge
 * into the site's analytics settings (`allStatuses`).
 *
 * Labels: core statuses keep their translated i18n label; custom ones fall back
 * to the label WooCommerce itself reports, so they read exactly as in wp-admin.
 *
 * Sites that have never synced fall back to the core WooCommerce set, i.e. the
 * behaviour before this hook existed.
 *
 * `checkout-draft` is excluded: it is an internal WooCommerce status for
 * abandoned block-checkout sessions, never something a user assigns.
 */
export function useOrderStatuses(siteId: string | null): OrderStatusChoice[] {
  const { t } = useTranslation();
  const { data: settings } = useAnalyticsSettings(siteId);

  return useMemo(() => {
    const synced = (settings?.allStatuses ?? [])
      .map((s) => ({ slug: s.slug?.replace(/^wc-/, ''), label: s.label }))
      .filter((s) => !!s.slug && s.slug !== 'checkout-draft');

    if (synced.length === 0) {
      return VALID_ORDER_STATUSES.map((s) => ({
        value: s,
        label: t(`orders.statuses.${s}`, s),
      }));
    }

    return synced.map(({ slug, label }) => ({
      value: slug!,
      label: t(`orders.statuses.${slug}`, label || slug!),
    }));
  }, [settings?.allStatuses, t]);
}
